import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getPblCases } from '@/api/pbl.api';
import PblCaseList from '@/components/Pbl/CaseList';
import {
  addPblDashboardFilterOption,
  getPblDashboardCaseFilter,
  getPblDashboardCaseStatuses,
  isPblDashboardCaseFilter,
  PBL_DASHBOARD_FILTER_PARAM,
} from '@/components/Pbl/CaseList/dashboardFilter';
import type { PblCaseListColumn, PblCaseListFilter, PblCaseListStatus } from '@/components/Pbl/CaseList/types';
import PATH from '@/router/path';
import { translatePblDashboardLabel } from '@/utils/pblDisplayTranslation';
import { errorHandler } from '@/utils/toastHandler';

import type { PblOcManagedCase } from '../OcManageCases/types';
import { isCitizenManagedCaseDetailAccessible, mapPblCasesToCitizenManagedCases } from './citizenCaseState';
import { useTranslation } from 'react-i18next';

const buildCaseDetailPath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const PblCitizenManageCases = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardFilter = getPblDashboardCaseFilter(searchParams);
  const [rows, setRows] = useState<PblOcManagedCase[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const citizenCaseColumns = useMemo<PblCaseListColumn[]>(
    () => [
      { key: 'caseId', label: t('pbl.pagesHardcoded.citizenManageCases.caseId') },
      { key: 'enquiryCategory', label: t('pbl.pagesHardcoded.citizenManageCases.enquiryCategory') },
      { key: 'applicantName', label: t('pbl.pagesHardcoded.citizenManageCases.applicantName') },
      { key: 'creationDate', label: t('pbl.pagesHardcoded.citizenManageCases.creationDate') },
      { key: 'status', label: t('pbl.pagesHardcoded.citizenManageCases.status') },
    ],
    [t]
  );
  const statusLabels = useMemo<Record<PblCaseListStatus, string>>(
    () => ({
      pending: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.pending'),
      in_progress: t('pbl.pagesHardcoded.citizenManageCases.inProgress'),
      to_be_rated: t('pbl.pagesHardcoded.citizenManageCases.toBeRated'),
      case_closed: t('pbl.pagesHardcoded.citizenManageCases.caseClosed'),
      case_cancelled: t('pbl.pagesHardcoded.citizenManageCases.caseCancelled'),
      to_be_confirmed: t('pbl.pagesHardcoded.citizenManageCases.toBeVerified'),
    }),
    [t]
  );
  const filters = useMemo<Array<{ value: PblCaseListFilter; label: string }>>(
    () => [
      { value: 'all', label: t('pbl.pagesHardcoded.citizenManageCases.all') },
      { value: 'to_be_confirmed', label: t('pbl.pagesHardcoded.citizenManageCases.toBeVerified') },
      { value: 'in_progress', label: t('pbl.pagesHardcoded.citizenManageCases.inProgress') },
      { value: 'to_be_rated', label: t('pbl.pagesHardcoded.citizenManageCases.toBeRated') },
      { value: 'case_closed', label: t('pbl.pagesHardcoded.citizenManageCases.caseClosed') },
      { value: 'case_cancelled', label: t('pbl.pagesHardcoded.citizenManageCases.caseCancelled') },
    ],
    [t]
  );
  const activeFilters = useMemo(
    () =>
      addPblDashboardFilterOption(
        filters,
        dashboardFilter,
        dashboardFilter
          ? translatePblDashboardLabel(t, { key: dashboardFilter, label: dashboardFilter })
          : ''
      ),
    [dashboardFilter, filters, t]
  );

  useEffect(() => {
    let isMounted = true;

    const loadCases = async () => {
      try {
        const dashboardStatuses = getPblDashboardCaseStatuses(dashboardFilter);
        const caseRequestParams = {
          ...(dashboardStatuses?.length === 1 ? { status: dashboardStatuses[0] } : {}),
          per_page: rowsPerPage,
          page: currentPage,
        };
        const response = await getPblCases(caseRequestParams);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.citizenManageCases.failedToLoadCitizenCases'));
          return;
        }

        if (!isMounted) return;

        const page = response.data.data;
        const nextRows = mapPblCasesToCitizenManagedCases(page.data);
        setRows(nextRows);
        setTotalEntries(page.total);
        setLastPage(page.last_page || 1);
      } catch (error: any) {
        if (isMounted) {
          errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.citizenManageCases.failedToLoadCitizenCases'));
        }
      }
    };

    loadCases();

    return () => {
      isMounted = false;
    };
  }, [currentPage, dashboardFilter, rowsPerPage]);

  const updateFilter = (filter: PblCaseListFilter) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (isPblDashboardCaseFilter(filter)) {
      nextSearchParams.set(PBL_DASHBOARD_FILTER_PARAM, filter);
    } else {
      nextSearchParams.delete(PBL_DASHBOARD_FILTER_PARAM);
    }

    setCurrentPage(1);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const changeRowsPerPage = (nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setCurrentPage(1);
  };

  return (
    <PblCaseList
      title={t('pbl.pagesHardcoded.citizenManageCases.manageCase')}
      rows={rows}
      columns={citizenCaseColumns}
      filters={activeFilters}
      initialFilter={dashboardFilter || 'all'}
      onFilterChange={updateFilter}
      totalEntries={totalEntries}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      lastPage={lastPage}
      onPageChange={setCurrentPage}
      onRowsPerPageChange={changeRowsPerPage}
      searchAriaLabel="Search citizen cases"
      statusLabels={statusLabels}
      isRowClickable={isCitizenManagedCaseDetailAccessible}
      onRowClick={(item) => navigate(buildCaseDetailPath(item.id))}
    />
  );
};

export default PblCitizenManageCases;
